import type { AxiosHeaders } from 'axios';
import { api } from './index';

const BASE_PATH = '/api/internal/caregivers';

export type Caregiver = {
  id: number;
  idCode: number;
  firstName: string;
  lastName: string;
  email: string;
  corporateEmail?: string;
  phoneNumber: string;
  countryId: string;
  countryName: string;
  address: string;
  latitude?: string;
  longitude?: string;
  dateOfBirth: Date;
  humanDateOfBirth: string;
  description: string;
  age?: number;
  dni: string;
  passportNumber?: string;
  sex: string;
  humanSex: string;
  career: string;
  humanCareer: string;
  fullName: string;
  communeId: string;
  communeName: string;
  comments: string;
  defaultWeekCost?: number;
  defaultWeekendCost?: number;
  defaultWithTaxBill?: boolean;
  employmentContractType?: string;
  humanEmploymentContractType?: string;
  workdayType?: string;
  humanWorkdayType?: string;
  agreedOvertimeCompensation?: boolean;
  compensationType?: string;
  humanCompensationType?: string;
  flexibleWorkingHours?: boolean;
  exceptionalWorkdayResolution?: string;
  exceptionalWorkdayDate?: string;
  exceptionalWorkdayText?: string;
  humanExceptionalWorkdayDate?: string;
  officeId?: number;
  officeName?: string;
  serviceCompanyId?: number;
  serviceCompanyName?: string;
  startsOn?: string;
  endsOn?: string;
  humanStartsOn?: string;
  humanEndsOn?: string;
  hasSalary?: boolean;
  contractedWithSalary?: boolean;
  groupIds?: number[];
  groups?: Array<{ id: number; name: string }>;
  skillIds?: number[];
  skills?: Array<{ id: number; name: string; career: string }>;
  // Name, RUT, career and archiving come from the company's ERP.
  erpManaged?: boolean;
}

export interface CaregiverForm {
  id?: number;
  idCode?: number;
  erpManaged?: boolean;
  firstName: string;
  lastName: string;
  email: string | null;
  corporateEmail?: string;
  // Read-only, from the server: whether the caregiver already created a Kuido Care password.
  passwordIsSet?: boolean;
  // Read-only, from the server: whether the caregiver already signed in to Kuido Care by any method.
  usesApp?: boolean;
  // Mail the "Crear contraseña" link after saving (ignored when a password exists).
  sendAppAccess?: boolean;
  phoneNumber: string;
  address: string;
  latitude?: string;
  longitude?: string;
  dateOfBirth: Date;
  description: string;
  dni: string;
  passportNumber?: string;
  sex: string;
  career: string;
  countryId: string;
  communeId: string;
  comments: string;
  employmentContractType: string;
  workdayType?: string;
  agreedOvertimeCompensation?: boolean;
  compensationType?: string;
  flexibleWorkingHours?: boolean;
  exceptionalWorkdayResolution?: string;
  exceptionalWorkdayDate?: string;
  exceptionalWorkdayText?: string;
  officeId?: number;
  serviceCompanyId?: number;
  startsOn?: string;
  endsOn?: string;
  skillIds?: number[];
}

export type XlsxCaregivers = {
  headers: AxiosHeaders;
  data: Blob;
}

export type CaregiverFilter = {
  id: number;
  fullName: string;
  career: string;
  humanCareer: string;
  contractedWithSalary?: boolean;
  groupIds?: number[];
}

// Slim shape returned by the caregivers index list view (for_list=true). Only the
// fields the list table/search and its discard modals use — not the full Caregiver
// payload (see Api::Internal::CaregiverListSerializer).
export type CaregiverListItem = {
  id: number;
  idCode: number;
  firstName: string;
  lastName: string;
  fullName: string;
  dni?: string;
  phoneNumber: string;
  communeName: string;
  career: string;
  humanCareer: string;
  // Archived and restored in the company's ERP, not in Kuido.
  erpManaged?: boolean;
}

export type CaregiverForGroupsManagement = {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  dni?: string;
  career: string;
  humanCareer: string;
  groupIds: number[];
  groups: Array<{ id: number; name: string }>;
}

export const caregiversApi = {
  index(scope?: string, queryParams?: string): Promise<Array<Caregiver>> {
    return api({
      method: 'get',
      url: `${BASE_PATH}?scope=${scope}&${queryParams}`,
    }).then(response => response.data.caregivers);
  },
  indexForList(scope?: string, queryParams?: string): Promise<Array<CaregiverListItem>> {
    return api({
      method: 'get',
      url: `${BASE_PATH}?for_list=true&scope=${scope}&${queryParams}`,
    }).then(response => response.data.caregivers);
  },
  indexForFilters(groupIdIn?: number[]): Promise<Array<CaregiverFilter>> {
    let url = `${BASE_PATH}?for_filters=true`;
    if (groupIdIn && groupIdIn.length > 0) {
      url += groupIdIn.map(id => `&group_id_in[]=${id}`).join('');
    }

    return api({ method: 'get', url }).then(response => response.data.caregivers);
  },
  indexForGroupsManagement(
    groupIdIn?: number[],
    ungrouped?: boolean,
  ): Promise<Array<CaregiverForGroupsManagement>> {
    let url = `${BASE_PATH}?for_groups_management=true`;
    if (groupIdIn && groupIdIn.length > 0) {
      url += groupIdIn.map(id => `&group_id_in[]=${id}`).join('');
    }
    if (ungrouped) url += '&ungrouped=true';

    return api({ method: 'get', url }).then(response => response.data.caregivers);
  },
  indexAsXlsx(scope?: string, groupIds?: number[]): Promise<XlsxCaregivers> {
    const groupParams = groupIds && groupIds.length > 0 ?
      `&${groupIds.map(id => `groups_id_in[]=${id}`).join('&')}` : '';

    return api({
      method: 'get',
      url: `${BASE_PATH}.xlsx?scope=${scope}${groupParams}`,
      responseType: 'blob',
    });
  },
  show(id: number): Promise<Caregiver> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}`,
    }).then(response => response.data.caregiver);
  },
  create(caregiverForm: CaregiverForm, groupIds?: number[]): Promise<Caregiver> {
    const data: Record<string, unknown> = { caregiver: caregiverForm };
    if (groupIds !== undefined) data.groupIds = groupIds;

    return api({
      method: 'post',
      url: BASE_PATH,
      data,
    }).then(response => response.data.caregiver);
  },
  update(caregiverForm: Partial<CaregiverForm>, groupIds?: number[]): Promise<Caregiver> {
    const data: Record<string, unknown> = { caregiver: caregiverForm };
    if (groupIds !== undefined) data.groupIds = groupIds;

    return api({
      method: 'put',
      url: `${BASE_PATH}/${caregiverForm.id}`,
      data,
    }).then(response => response.data.caregiver);
  },
  updateGroups(id: number, groupIds: number[]): Promise<void> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}/group_memberships`,
      data: { groupIds },
    }).then(() => {});
  },
  updateDefaultCosts(
    caregiverId: number,
    pricingData: {
      defaultWeekCost: number;
      defaultWeekendCost: number;
      defaultWithTaxBill: boolean
    },
  ): Promise<Caregiver> {
    return api({
      method: 'patch',
      url: `${BASE_PATH}/${caregiverId}`,
      data: { caregiver: pricingData },
    }).then(response => response.data.caregiver);
  },
  destroy(id: number): Promise<void> {
    return api({
      method: 'delete',
      url: `${BASE_PATH}/${id}`,
    }).then(() => {});
  },
};
